import { ComponentRef, Directive, ElementRef, Input, NgZone, OnDestroy, OnInit, Renderer2, ViewContainerRef, inject } from '@angular/core';
import {PopoverComponent} from "../../popover/popover.component";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {PopoverManagerService} from "./services/popover-manager.service";
import {filter} from "rxjs/operators";

@UntilDestroy()
@Directive({
    selector: '[appCustomPopover]',
    standalone: false
})
export class CustomPopoverDirective implements OnDestroy, OnInit {
    private readonly elementRef = inject(ElementRef);
    private readonly vcr = inject(ViewContainerRef);
    private readonly renderer = inject(Renderer2);
    private readonly ngZone = inject(NgZone);
    private readonly popoverManagerService = inject(PopoverManagerService);


    @Input() placement: 'top' | 'bottom' | 'left' | 'right' = 'top';
    @Input() popoverType: 'default' | 'warning' | 'error' = 'default';
    @Input() popoverTextParam: string;
    @Input() adaptPlacement = false;

    @Input()
    set popoverDelay(delay: string) {
        this.delay = Number(delay);
    };

    @Input() popoverText: string;

    @Input()
    set popoverTextValidation(value: string) {
        this._popoverTextValidation = value.length > 0;
        this.popoverText = value;
        if (this._popoverTextValidation) {
            this.showPopover();
        } else {
            this.hidePopover();
        }
    }

    private _popoverTextValidation: boolean;
    private popoverRef: ComponentRef<PopoverComponent>;
    private popoverId: string;

    private OFFSET_PX = 10;
    private delay = 0;
    private stateVisible = false;
    private placementAdapted = false;
    private initialPlacement: 'top' | 'bottom' | 'left' | 'right' = 'top';
    private showPopoverBound: EventListenerOrEventListenerObject;
    private hidePopoverBound: EventListenerOrEventListenerObject;
    private mutationObserver: MutationObserver;

    ngOnInit() {
        this.popoverId = Math.random().toString(36).substring(6);

        if (this._popoverTextValidation === undefined) {
            this.showPopoverBound = this.showPopover.bind(this);
            this.hidePopoverBound = this.hidePopover.bind(this);

            this.ngZone.runOutsideAngular(() => {
                this.elementRef.nativeElement.addEventListener('mouseenter', this.showPopoverBound);
                this.elementRef.nativeElement.addEventListener('mouseleave', this.hidePopoverBound);
            });

            this.popoverManagerService.closeAllPopovers
                .pipe(
                    untilDestroyed(this),
                    filter(() => this.popoverRef !== undefined && this.popoverRef !== null)
                )
                .subscribe(() => {
                    this.hidePopover();
                });
        } else {

            this.mutationObserver = new MutationObserver(() => {
                this.positionPopover();
            });
            this.mutationObserver.observe(document.body, {
                attributes: true,
                attributeFilter: ['style', 'zoom']
            });
        }
    }

    ngOnDestroy() {
        if (this._popoverTextValidation === undefined) {
            this.elementRef.nativeElement.removeEventListener('mouseenter', this.showPopoverBound);
            this.elementRef.nativeElement.removeEventListener('mouseleave', this.hidePopoverBound);
        } else {
            this.mutationObserver.disconnect();
        }

        this.hidePopover();
    }

    private showPopover(): void {
        this.ngZone.run(() => {
            if (!this.popoverRef && this.popoverText) {
                this.popoverManagerService.setActivePopover(this.popoverId);
                this.initiatePopover();
            }
        });
    }

    private hidePopover(): void {
        this.ngZone.run(() => {
            if (this.popoverRef) {
                this.popoverRef.destroy();
                this.popoverRef = null;
            }
        });
    }

    private initiatePopover(): void {
        this.stateVisible = true;
        setTimeout(() => {
            if (this.stateVisible && !this.popoverRef) {
                this.popoverRef = this.vcr.createComponent(PopoverComponent);
                this.popoverRef.instance.popoverText = this.popoverText;
                this.popoverRef.instance.placement = this.placement;
                this.initialPlacement = this.placement;
                this.popoverRef.instance.popoverType = this.popoverType;
                this.popoverRef.instance.popoverTextParam = this.popoverTextParam;
                const domElem = this.popoverRef.location.nativeElement;
                document.body.appendChild(domElem);
                this.popoverRef.instance.viewReady
                    .pipe(untilDestroyed(this))
                    .subscribe(() => {
                        this.positionPopover();
                        if (this.popoverManagerService.getActivePopoverId() !== this.popoverId) {
                            this.hidePopover();
                        }
                    });
            }
        }, this.delay);
    }

    private positionPopover(): void {
        if (!this.popoverRef) {
            return;
        }
        this.placementAdapted = false;

        const popoverElement = this.popoverRef.location.nativeElement as HTMLElement;
        const targetElement = this.elementRef.nativeElement as HTMLElement;

        // Get the current zoom level
        const zoomLevel = parseFloat(window.getComputedStyle(document.body).zoom);

        // Get bounding rectangles
        const targetRect = targetElement.getBoundingClientRect();
        const popoverRect = popoverElement.getBoundingClientRect();

        // Calculate position (e.g., above the target element)
        let top = 0;
        let left = 0;

        for (let i = 0; i < 1 || !this.placementAdapted && this.adaptPlacement; i++) {
            switch (this.placement) {
            case 'top':
                top = targetRect.y - popoverRect.height - this.OFFSET_PX;
                left = targetRect.x + (targetRect.width / 2) - popoverRect.width / 2;
                break;
            case 'bottom':
                top = targetRect.y + targetRect.height + this.OFFSET_PX;
                left = targetRect.x + (targetRect.width - popoverRect.width) / 2;
                break;
            case 'left':
                top = targetRect.y + (targetRect.height - popoverRect.height) / 2;
                left = targetRect.left - popoverRect.width - this.OFFSET_PX;
                break;
            case 'right':
                top = targetRect.y + (targetRect.height - popoverRect.height) / 2;
                left = targetRect.right + this.OFFSET_PX;
                break;
            default:
                break;
            }
            // Adjust for zoom level
            top = top / zoomLevel;
            left = left / zoomLevel;

            if (this.adaptPlacement) {

                const documentRect = document.body.getBoundingClientRect();
                this.placementAdapted = documentRect.top < top && documentRect.bottom > top &&
                    documentRect.left < left && documentRect.right > left + popoverRect.width;
                if (!this.placementAdapted) {
                    switch (i) {
                    case 0:
                        break;
                    case 1:
                        this.placement = "top";
                        break;
                    case 2:
                        this.placement = "right";
                        break;
                    case 3:
                        this.placement = "bottom";
                        break;
                    case 4:
                        this.placement = "left";
                        break;
                    default:
                        this.placement = this.initialPlacement;
                        this.placementAdapted = true;
                        break;
                    }
                }
            }
        }

        // Apply position styles
        this.renderer.setStyle(popoverElement, 'top', `${top}px`);
        this.renderer.setStyle(popoverElement, 'left', `${left}px`);
        this.renderer.removeClass(popoverElement, this.initialPlacement);
        this.renderer.addClass(popoverElement, this.placement);
    }
}
